/**
 * @file        jnu/index.js  XHR-based Database connector for Ormdb, main entry point.
 *                            This library makes RESTful queries to the Jnu back end services, and
 *                            provides high-level APIs based on pormdb.
 *
 * @author      Wes Garland, wes@distributive.network
 * @date        July 2023
 */
'use strict';

const wallet   = require('dcp/dcp-client/wallet');
const identity = require('dcp/dcp-client/identity');

const { assert }                      = require('dcp/common/dcp-assert');
const { submitSignedPost,
        determineTimeServiceLocation,
        serverRelativeTime }          = require('dcp/utils');

const KVIN = require('./jnu-kvin.js').KVINv9;
KVIN.userCtors.ColumnType = function ColumnType(){};
exports.KVIN = KVIN;

const schemas = {};

/**
 * Connect (in the RESTful not-really sense) to a remote database server. This routine ensures that we
 * have basic network connectivity, downloads the database schema corresponding to the service, forces
 * us to come up with some kind of identity (wallet.getId), and returns a handle which can be used for
 * subsequent queries.
 *
 * @param {string} service     The service we want to query; must match a service in the dcpConfig, eg.
 *                             'portal', 'pxAuth', 'scheduler', which has a jnu property.
 * @param {Keystore} id        [option] The identity to connect as; the default is to use identity.get()
 *                             to determine the current identity, with a fallback to wallet.getId()
 *                             until that API is deprecated.
 * @returns Promise which resolves to an instance of exports.Connection.  This object will have the
 *          schema property defined, and the columns in the schema will correctly have all of the
 *          columns of a given column type all being the same object.
 */
exports.connect = async function jnu$$connect(service, id)
{
  if (!id)
  {
    if (identity.check())
      id = await identity.get();
    else /* fallback to wallet API for identity during transitional period /wg july 2024 */
    {
      wallet.addId(await wallet.getId());   /* bug work around */
      id = await wallet.getId();
    }
  }
  assert(id instanceof wallet.Keystore || id instanceof wallet.PrivateKey);

  const db = new exports.Connection(service, id);
  if (!schemas[service])
  {
    /* schema not access-protected nor identity-variant: cache it */
    const schemaQuery = await exports.makeQuery(db, 'schema', { KVINv9: true });
    const result = await db.performQuery(schemaQuery);
    const schema = KVIN.unmarshal(result.schemaKVIN);
    /* Use embedded schema template to populate classes and serializers */
    const template = require('./generated/schemas/blank');
    Object.assign(schema.classes, template.classes);
    Object.assign(schema.serializers, template.serializers);
    schemas[service] = schema;
  }

  db.schema = schemas[service];
  if (service === 'scheduler')
  {
    /* A mis-match in here means that the column-type relationships are probably not getting serialized
     * properly; the object-identity of the specific tables[tableName][columnName] to the columnTypes in
     * are handled by KVIN and its ability to represent a wide variety of digraphs.
     */
    assert(db.schema.tables.jobs?.id === db.schema.columnTypes.autoId);
    assert(db.schema.tables.jobs?.id === db.schema.tables.jobResults?.id);
  }

  return db;
}

/**
 * Connection constructor.
 * Instance of Connection represent a database handle which references a specific Jnu URL, database
 * schema, and DCP identity.
 *
 * @param {string}           serviceName     The name of the DCP service whose database we want to
 *                                           query
 * @param {Keystore|Promise} idKs            Optional eystore (or a Promise which resolves to a
 *                                           Keystore) respresenting the entity making the requests.
 *                                           All messages to Jnu are signed with this Keystore, and
 *                                           queries are limited based on what they are who is making
 *                                           by the back end (Jnu); de-proxying as necessary. If idKs
 *                                           is not provided, we pull from DCP wallet, potentially
 *                                           invoking the oAuth flow.
 * @returns Promise which resolves to an instance of Connection. Once the Promise has resolved,
 *          this.identity has been set to an instance of Keystore.
 */
exports.Connection = function jnu$$Connection(serviceName, idKs)
{
  this.config = dcpConfig[serviceName].services.jnu;
  this.identity = idKs;

  assert(this.config.location?.href);
  assert(this.identity.address instanceof wallet.Address);
}

exports.Connection.prototype = new (require('events').EventEmitter)('jnu-db::Connection');

/**
 * Calculate the endpoint URL of the jnu service based on the database connection config and
 * requested method.
 */
exports.Connection.prototype.endpointOf = function jnu$$Connection$endpointOf(method)
{
  if (method === '' || typeof method !== 'string')
    throw new Error('invalid method: ' + method);

  return this.config.location.origin + this.config.location.pathname + method;
}

/**
 * Calculate the number ms since the epoch from the Jnu server's perspective.
 * @param {number} interval       offset from now, in seconds
 * @param {any}    now            [optional] default = now, the Date on the client that we are trying to
 *                                store on the server.
 * @returns {number}
 * @throws if used before schema is loaded
 *
 * @example
 * record.creationDate = db.serverRelativeTime();
 */
exports.Connection.prototype.serverRelativeTime = function jnu$$Connection$serverRelativeTime(interval, now)
{
  const endpoint = this.endpointOf('schema'); /* time is the same for all endpoints on one jnu connection */
  const timeServiceLocation = determineTimeServiceLocation(endpoint, {
    timeServiceLocation: this.config.timeServiceLocation
  });
  return serverRelativeTime({ timeServiceLocation }, interval, now);
}

/**
 * Low-level function to execute a database query.
 * @param {object} query           the query to execute, formatted for decoding by the dcp
 *                                 signed-post library.
 * @returns {object} the result of the query
 */
exports.Connection.prototype.performQuery = async function jnu$$Connection$performQuery(query)
{
  this.emit('busy');
  try
  {
    const { timeServiceLocation } = this.config;
    const result = await submitSignedPost(this.identity, query, { timeServiceLocation });
    if (result instanceof Error)
    {
      this.emit(result);
      throw result;
    }
    return result;
  }
  catch (error)
  {
    this.emit(error);
    throw error;
  }
  finally
  {
    this.emit('unbusy');
  }
}

exports.Connection.prototype.makeQuery = function jnu$$Connection$makeQuery(...args)
{
  return exports.makeQuery(this, ...args);
}

exports.Connection.prototype.getPrimaryKeyColumnName = function jnu$$Connection$getPrimaryKeyColumnName(tableName)
{
  const tableSchema = this.schema.tables[tableName];
  return Object.entries(tableSchema).filter(kvp => { const [key,value] = kvp; return value.primaryKey})[0][0];
}

exports.Connection.prototype.change = async function jnu$$Connection$change(query, __options)
{
  if (!__options)
    __options = { variant: 'change' };
  const preparedQuery = await this.makeQuery('change', query, __options);
  return (await this.performQuery(preparedQuery)).result;
}

exports.Connection.prototype.changeOrAdd = function jnu$$Connection$changeOrAdd(query)
{
  return this.change(query, { variant: 'changeOrAdd' });
}

exports.Connection.prototype.changeMany = function jnu$$Connection$changeMany(query)
{
  return this.change(query, { variant: 'changeMany' });
}

exports.Connection.prototype.add = async function jnu$$Connection$add(query, __options)
{
  if (!__options)
    __options = { variant: 'add' };
  const preparedQuery = await this.makeQuery('add', query, __options);
  return (await this.performQuery(preparedQuery)).result;
}

exports.Connection.prototype.delete = async function jnu$$Connection$delete(query, __options)
{
  if (!__options)
    __options = { variant: 'delete' };
  const preparedQuery = await this.makeQuery('delete', query, __options);
  return (await this.performQuery(preparedQuery)).result;
}

exports.Connection.prototype.deleteMany = function jnu$$Connection$deleteMany(query)
{
  return this.delete(query, { variant: 'deleteMany' });
}

/**
 * Make a result set from the response of a call to the Jnu select method.
 *
 * In the future, this will be converted to a streaming API so that we can support huge
 * result sets without
 * a) loading them all into memory at once, and
 * b) forcing pagination workarounds on application developer via limit/skip
 *
 * @param {Object}       res the unmarshaled response object from the Jnu select method
 *
 * @returns a fake streaming result set, which has the following API
 *   - async rows.map()      // like Array.map,     but async, iterates over coercedRows
 *   - async row.forEach()   // like Array.forEach, but async, iterates over coercedRows
 *   - rows [0]              // the first element in the set
 *   - async getFirst()      // the first element in the set, resetting the cursor
 *   - async getNext()       // the next element in the set (first if never)
 *   - rowCount              // the number of elements in the set
 */
exports.ResultSet = function jnu$$Connection$ResultSet(resultsHeader)
{
  var cursorPos;

  this.rowCount = resultsHeader.rowCount;
  if (resultsHeader.hasOwnProperty('totalRowCount'))
    this.totalRowCount = resultsHeader.totalRowCount;

  /* forEach, map faked with Array internals until streaming implementation */
  const rows = this.rowCount ? [ resultsHeader.firstRow ].concat(resultsHeader.remainingRows) : [];
  this.rows = new Proxy([rows[0]], {
    get: function jnuDb$ResultSet$getter(obj, prop) {
      switch(prop)
      {
        case 'forEach': return async function jnuDb$ResultSet$rows$forEach() { cursorPos=rows.length; return rows.forEach.apply(rows, arguments); }; // eslint-disable-line require-await
        case 'map':     return async function jnuDb$ResultSet$rows$map()     { cursorPos=rows.length; return rows.map.apply    (rows, arguments); }; // eslint-disable-line require-await
        case 0:
        case '0':       return rows[0];
      }

      if ((typeof prop === 'number' || typeof prop === 'string' && Number(prop)) > 0)
        throw new Error('random access into result sets is not supported');

      return undefined;
    },
    set: function jnuDb$ResultSet$setter(obj, prop, value) {
      return false;
    }
  });

  /**
   * Return the "next" element of the result set.
   * - if this is the first call to getFirst/getNext, the first element is retrieved
   * - if there are no more rows to return, return false
   * - otherwise, advance the internal cursor and return the row it points at
   */
  this.getNext = async function jnu$$getNext() {
    if (cursorPos === undefined)
      return await this.getFirst();
    if (cursorPos >= rows.length)
      return false;
    return rows[cursorPos++];
  };

  /* Reset the getNext cursor to the beginning of the result set, and return the first element */
  this.getFirst = async function jnu$$getFirst() {
    cursorPos = 1;
    return rows.length ? rows[0] : false;
  };
}

/**
 * Retrieve rows from the database.
 * @see ormdb::Connection:select for all the fancy query __options.
 * @returns A promise which rejects with an instance of Error, or resolves with an instance of
 */
exports.Connection.prototype.select = async function jnu$$Connection$select(query, __options)
{
  __options = Object.assign({}, __options);

  if (!__options.hasOwnProperty('count') && query.hasOwnProperty('limit') && query.hasOwnProperty('skip'))
    __options.count = true;

  const preparedQuery = await this.makeQuery('select', query, __options);
  const res = await this.performQuery(preparedQuery);
  const resultsHeader = KVIN.unmarshal(res.resultsHeaderKVIN); /* props rowCount, firstRow, remainingRows, totalCount? */
  if (typeof resultsHeader.rowCount === 'undefined')
    return resultsHeader;

  return new exports.ResultSet(resultsHeader);
}

exports.Connection.prototype.search = function jnu$$Connection$search (query)
{
  return this.select(query, { variant: 'search' });
}

exports.Connection.prototype.isearch = function jnu$$Connection$isearch (query)
{
  return this.select(query, { variant: 'isearch' });
}

exports.Connection.prototype.groupCount = function jnu$$Connection$groupCount (query, groups)
{
  return this.select(query, { variant: 'groupCount', groups: groups });
}

exports.Connection.prototype.count = function jnu$$Connection$count (query)
{
  return this.select(query, { variant: 'count' });
}

exports.Connection.prototype.selectSum = function jnu$$Connection$selectSum (columnName, query)
{
  return this.select(query, { variant: 'sum', columnName });
}

exports.Connection.prototype.selectMax = function jnu$$Connection$selectMax (columnName, query)
{
  return this.select(query, { variant: 'max', columnName });
}

exports.Connection.prototype.selectMin = function jnu$$Connection$selectMin (columnName, query)
{
  return this.select(query, { variant: 'min', columnName });
}

exports.Connection.prototype.selectAvg = function jnu$$Connection$selectAvg (columnName, query)
{
  return this.select(query, { variant: 'avg', columnName });
}

exports.Connection.prototype.selectDistinct = function jnu$$Connection$selectDistinct (columnName, query)
{
  return this.select(query, { variant: 'distinct', columnName });
}

/**
 * Factory function for instanciating Connection. Syntactic sure to make the async constructor
 * easier to deal with with.
 *
 * @returns a Promise which resolves to an instance of Connection
 */
exports.ConnectionFactory = async function jnu$$ConnectionFactory(...args) {
  return await new exports.Connection(...args);
}

/**
 * Make a query from an ormdb-style query object, suitable for submission to Jnu.
 * @param {Connection}   db          the database the request is for
 * @param {string}       method      the query method (eg select, change, delete)
 * @param {Object}       query       the query object - get serialized and passed to ormdb in Jnu
 * @param {Object}       options     optional per-method options object
 * @param {Date|number}  expiry      optional time at which the cryptographic signature authorizing
 *                                   this method expires. If passing a number, this is a UNIX
 *                                   time_t, eg number of seconds since the epoch. This time should
 *                                   be relative to the local clock, the underlying API will adjust as
 (                                   necessary based on the endpoint in the query object.
 *
 * @return a Promise which resolves to an Object which can be signed then stringified with JSON and submitted to
 *         Jnu. This object has a property endpoint which is a string that encodes the endpoint
 *         URL in terms of what the endpoint thinks its location is.
 */
exports.makeQuery = async function jnu$$makeQuery(db, method, query, options, expiry)
{
  assert(db instanceof exports.Connection);
  assert(method && typeof method === 'string')
  assert(query);

  if (method !== 'schema')
  {
    if (!query.table)
      throw new Error(`${method} query missing table name`);
    if (!query.prototype)
      throw new Error(`${method} ${query.table} query missing prototype`);
  }

  if (expiry instanceof Date)
    expiry = expiry.valueOf() / 1000;
  else if (!expiry)
    expiry = Math.floor(Date.now()/1000) + (db.config.defaultExpiry || 3600);

  const endpoint = db.endpointOf(method);
  return { expiry, endpoint, queryKVINv9: KVIN.marshal(query), options };
}
